import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { STORAGE_NAMES, TOP_STYLE_IDS, TOP_STYLE_PELMET, VIEW_TYPES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { BlindData, Frames, IBlindSelect, SectionKeysEnum, TopStyle } from '@root/app.interfaces';
import { VariantService } from '@core/services/variant/variant.service';
import { EngineService } from '@core/services/engine/engine.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { FrameColorComponent } from '@features/frame-color/frame-color.component';

@Component({
	selector: 'app-top-style',
	templateUrl: './top-style.component.html',
	styleUrls: ['./top-style.component.scss', './top-style.responsive.scss'],
})
export class TopStyleComponent implements OnInit, OnDestroy, OnChanges {
	@Input() blindId: number;

	@Output() frameData: EventEmitter<{ frames: Frames }> = new EventEmitter();

	@ViewChild('frameTopStyleItem') frameTopStyleItem: ElementRef<HTMLElement>;

	sessionText = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);
	sessionConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config);
	blindType: 'interior' | 'outdoor';
	isDesignType: boolean = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;

	defaultMeshes = TOP_STYLE_PELMET;
	framesData: { frames: Frames };
	topStyle: TopStyle[];
	frameTopStyleObject = {
		id: 0,
		name: '',
		meshes: [],
	};

	dontSetTopStyle = this.variant.isShop();

	blindSelectSubscription: Subscription;

	constructor(
		private readonly variant: VariantService,
		private readonly engineService: EngineService,
		private readonly shareDataService: ShareService,
		private readonly changeDetection: ChangeDetectorRef,
		private readonly blindStateService: BlindStateService,
		private readonly saveSessionBlind: SaveSessionBlindsService,
		private readonly sessionStorageService: SessionStorageService,
	) {}

	ngOnInit() {
		const blind = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		this.blindType = blind?.type;
		this.getFrameFromStorage();

		if (this.blindType === 'outdoor') {
			this.topStyle = this.sessionConfig?.[this.blindType].frame.top_style;
		}

		this.blindSelectSubscription = this.shareDataService.getBlindSelect.subscribe(this.setBlindSelectId.bind(this));
	}

	ngOnChanges(changes: SimpleChanges): void {
		const getCurrentBLindId = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);

		if (changes.blindId.currentValue === getCurrentBLindId) {
			setTimeout(this.setDefaultValue.bind(this));
		}
	}

	ngOnDestroy() {
		this.blindSelectSubscription.unsubscribe();
	}

	getFrameFromStorage(id?: number | string): void {
		const blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const blinds = this.sessionStorageService.getBlindItemById(id || this.blindId, STORAGE_NAMES.zip_blind_data);

		if (blinds) {
			for (const blind of blinds) {
				if (blind.setup && blind.setup.frames && id === this.blindId) {
					const { top_style } = blind.setup.frames;
					this.dontSetTopStyle = top_style ? !blind.setup.frames.top_style.configured : this.dontSetTopStyle;
					this.framesData = { frames: { ...blind.setup.frames } };

					if (blindType === 'outdoor') {
						this.frameTopStyleObject = top_style.configured ? top_style : { ...this.frameTopStyleObject, name: ' ' };
					}
				}
			}
		}
	}

	setDefaultValue(): void {
		const blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const frameStorage = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const frameConfig = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config)[blindType].frame;

		if (_.isEmpty(this.framesData?.frames)) {
			this.framesData = {
				frames: {
					frame_color: {
						id: null,
						name: null,
						color: null,
						color_name: null,
					},
					top_style: {
						id: null,
						name: null,
					},
					bottom_bar: {
						id: null,
						name: null,
					},
					optionals: [],
				},
			};
		}

		if (_.isEmpty(frameStorage && frameStorage.setup.frames?.top_style) && frameConfig.top_style) {
			for (const topStyle of frameConfig.top_style) {
				if (topStyle.is_default) {
					this.onSelectTopStyle(topStyle, null, false);
				}
			}
		}

		if (!this.isDesignType && !_.isEmpty(frameStorage)) {
			this.setBlindSelectId({ id: this.blindId });
		}
	}

	setBlindSelectId(data: IBlindSelect): void {
		this.getFrameFromStorage(data.id);
		this.blindType = this.sessionStorageService.getBlindItemById(data?.id, STORAGE_NAMES.zip_blind_data)[0].type;

		if (this.blindType === 'outdoor') {
			const topStyleData = TOP_STYLE_IDS.filter((item) => item.id === this.frameTopStyleObject?.id);

			this.topStyle = this.sessionConfig[this.blindType].frame.top_style;
			this.frameTopStyleObject.meshes = topStyleData[0]?.meshes || this.defaultMeshes;
		}

		if (data.id === this.blindId) {
			if (this.blindType === 'outdoor') {
				const topStyleMeshes =
					_.isEmpty(this.frameTopStyleObject) && _.isEmpty(this.frameTopStyleObject.meshes)
						? this.defaultMeshes
						: this.frameTopStyleObject.meshes;

				this.engineService.setTopStyle({
					meshes: topStyleMeshes,
					type: 'storage',
				});
			}
		}

		this.changeDetection.markForCheck();
	}

	onSelectTopStyle(data: TopStyle, event: Event, saveChangeTime = true, buttonPress = false): void {
		if (buttonPress) {
			this.dontSetTopStyle = false;
		}

		if (event) {
			FrameColorComponent.activeClassHandler(this.frameTopStyleItem.nativeElement, event.currentTarget as HTMLElement);
		}

		const topStyleMeshes = TOP_STYLE_IDS.filter((item) => item.id === data.id)[0].meshes;

		const topStyleData = {
			top_style: {
				id: data.id,
				name: data.name,
				configured: !this.dontSetTopStyle,
				is_extra_fee: data.is_extra_fee,
			},
		};
		this.engineService.setTopStyle({
			meshes: topStyleMeshes,
			type: event?.type,
			description: data.description,
		});

		this.framesData = { frames: { ...this.framesData.frames, ...topStyleData } };
		this.saveBlindSetup(this.framesData, saveChangeTime);
	}

	saveBlindSetup(framesData = this.framesData, saveChangeTime = true): void {
		const blind: BlindData = this.sessionStorageService.getBlindItemById(this.blindId, STORAGE_NAMES.zip_blind_data)[0];
		const { top_style } = framesData.frames;
		framesData.frames = {
			...blind.setup.frames,
			top_style,
		};
		this.frameData.emit(framesData);
		this.sessionStorageService.setBlindSetupDataById(this.blindId, framesData, STORAGE_NAMES.zip_blind_data, saveChangeTime);
		this.saveSessionBlind.PutStorageDataToServer();

		if (!this.dontSetTopStyle) {
			this.blindStateService.markConfigured(this.blindId, SectionKeysEnum.TopStyle);
		}
	}
}
